import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import type DealStage from "@/model/interface/DealStage";
import {
  DEAL_STAGE_CATEGORY_DESCRIPTIONS,
  DEAL_STAGE_CATEGORY_LABELS,
  DEAL_STAGE_CATEGORY_ORDER,
  DealStageCategory,
  stageCategoryOf,
} from "@/enum/DealStageCategory";
import {
  useCreateDealStageMutation,
  useGetDealStageSettingsQuery,
  useGetDealStagesQuery,
  useGetPipelineSummaryQuery,
  useMakeDefaultDealStageMutation,
  useReorderDealStagesMutation,
  useUpdateDealStageConfigMutation,
  useUpdateLeadAutoPromoteMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import DealStageRow from "@/components/pages/settings/deal-stages/DealStageRow";
import DealStageFormDialog, { type DealStageFormData } from "@/components/pages/settings/deal-stages/DealStageFormDialog";
import DeleteDealStageDialog from "@/components/pages/settings/deal-stages/DeleteDealStageDialog";

interface StageFormTarget {
  category: DealStageCategory;
  stage?: DealStage;
}

const ADD_STAGE_LABELS: Record<DealStageCategory, string> = {
  [DealStageCategory.LEAD]: "Add lead stage",
  [DealStageCategory.PIPELINE]: "Add pipeline stage",
  [DealStageCategory.WON]: "Add won stage",
  [DealStageCategory.LOST]: "Add lost stage",
};

const NEW_STAGE_COLOR = "#3B82F6";

function isClosedCategory(category: DealStageCategory) {
  return category === DealStageCategory.WON || category === DealStageCategory.LOST;
}

export default function DealStagesSettingsPage() {
  const { data: stages = [] as DealStage[], isLoading } = useGetDealStagesQuery({});
  const { data: summary } = useGetPipelineSummaryQuery();
  const { data: settings } = useGetDealStageSettingsQuery();
  const [createDealStage, { isLoading: isCreating }] = useCreateDealStageMutation();
  const [updateDealStageConfig, { isLoading: isUpdating }] = useUpdateDealStageConfigMutation();
  const [reorderDealStages] = useReorderDealStagesMutation();
  const [makeDefaultDealStage] = useMakeDefaultDealStageMutation();
  const [updateLeadAutoPromote, { isLoading: isSavingAutoPromote }] = useUpdateLeadAutoPromoteMutation();
  const [formTarget, setFormTarget] = useState<StageFormTarget | null>(null);
  const [stageToDelete, setStageToDelete] = useState<DealStage | null>(null);
  const [draggedStageId, setDraggedStageId] = useState<string | null>(null);
  const [autoPromote, setAutoPromote] = useState(true);

  const activeStages = stages.filter((stage) => stage.isActive);

  const handleSave = async (values: DealStageFormData) => {
    if (!formTarget) {
      return;
    }
    try {
      if (formTarget.stage) {
        await updateDealStageConfig({
          id: formTarget.stage.id,
          data: {
            name: values.name,
            color: values.color,
            description: formTarget.stage.description,
            category: stageCategoryOf(formTarget.stage),
          },
        }).unwrap();
        toast.success("Stage updated");
      } else {
        await createDealStage({ name: values.name, color: values.color, category: formTarget.category }).unwrap();
        toast.success("Stage added");
      }
      setFormTarget(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the stage"));
    }
  };

  const handleMakeDefault = async (stage: DealStage) => {
    try {
      await makeDefaultDealStage(stage.id).unwrap();
      toast.success(`New deals now start in ${stage.name}`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not change the default stage"));
    }
  };

  const moveDraggedStage = async (targetCategory: DealStageCategory, beforeStageId?: string) => {
    const draggedStage = activeStages.find((stage) => stage.id === draggedStageId);
    setDraggedStageId(null);
    if (!draggedStage || draggedStage.id === beforeStageId) {
      return;
    }
    if (draggedStage.isDefault && isClosedCategory(targetCategory)) {
      toast.error("The default stage cannot be a Won or Lost stage");
      return;
    }
    const order = DEAL_STAGE_CATEGORY_ORDER.flatMap((category) => {
      const entries = activeStages
        .filter((stage) => stage.id !== draggedStage.id && stageCategoryOf(stage) === category)
        .map((stage) => ({ id: stage.id, category }));
      if (category !== targetCategory) {
        return entries;
      }
      const moved = { id: draggedStage.id, category: targetCategory };
      const insertAt = beforeStageId ? entries.findIndex((entry) => entry.id === beforeStageId) : -1;
      if (insertAt === -1) {
        return [...entries, moved];
      }
      return [...entries.slice(0, insertAt), moved, ...entries.slice(insertAt)];
    });
    const unchanged = order.every(
      (entry, position) =>
        entry.id === activeStages[position]?.id && entry.category === stageCategoryOf(activeStages[position]),
    );
    if (unchanged) {
      return;
    }
    try {
      await reorderDealStages(order).unwrap();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not reorder the stages"));
    }
  };

  const handleAutoPromoteToggle = async (enabled: boolean) => {
    setAutoPromote(enabled);
    try {
      await updateLeadAutoPromote(enabled).unwrap();
      toast.success("Setting updated");
    } catch (error) {
      setAutoPromote(!enabled);
      toast.error(apiErrorMessage(error, "Failed to update setting"));
    }
  };

  useEffect(() => {
    if (settings) {
      setAutoPromote(settings.leadAutoPromote);
    }
  }, [settings]);

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 text-foreground">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full max-w-3xl" />
      </div>
    );
  }

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Deal stages</h1>
        <p className="text-muted-foreground">Drag stages between groups to choose when a lead becomes a contact.</p>
      </div>

      <div className="max-w-3xl space-y-6">
        <Card>
          <CardContent className="divide-y divide-border p-0">
            {DEAL_STAGE_CATEGORY_ORDER.map((category) => {
              const categoryStages = activeStages.filter((stage) => stageCategoryOf(stage) === category);
              return (
                <section
                  key={category}
                  className="space-y-3 p-4"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    void moveDraggedStage(category);
                  }}
                >
                  <div>
                    <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {DEAL_STAGE_CATEGORY_LABELS[category]}
                    </h2>
                    <p className="text-sm text-muted-foreground">{DEAL_STAGE_CATEGORY_DESCRIPTIONS[category]}</p>
                  </div>
                  {categoryStages.length > 0 ? (
                    <ul className="space-y-1.5">
                      {categoryStages.map((stage) => (
                        <DealStageRow
                          key={stage.id}
                          stage={stage}
                          dealCount={summary?.stageCounts[stage.id] ?? 0}
                          canBeDefault={!isClosedCategory(category)}
                          isDragging={draggedStageId === stage.id}
                          onEdit={() => setFormTarget({ category, stage })}
                          onMakeDefault={() => void handleMakeDefault(stage)}
                          onDelete={() => setStageToDelete(stage)}
                          onDragStart={() => setDraggedStageId(stage.id)}
                          onDragEnd={() => setDraggedStageId(null)}
                          onDropBefore={() => void moveDraggedStage(category, stage.id)}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                      No stages here. Drag one in or add one.
                    </p>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setFormTarget({ category })}>
                    <Plus className="mr-1 h-4 w-4" />
                    {ADD_STAGE_LABELS[category]}
                  </Button>
                </section>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leads without a deal</CardTitle>
            <CardDescription>
              Imported leads have no deal until someone promotes them or they get in touch.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-row items-start justify-between rounded-lg border p-4">
              <div className="space-y-1 pr-4">
                <p className="text-base font-medium">Promote on first reply</p>
                <p className="text-sm text-muted-foreground">
                  A lead's first reply or answered call starts a deal in the default stage.
                </p>
              </div>
              <Switch
                checked={autoPromote}
                disabled={!settings || isSavingAutoPromote}
                onCheckedChange={handleAutoPromoteToggle}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <DealStageFormDialog
        open={!!formTarget}
        title={formTarget?.stage ? `Edit ${formTarget.stage.name}` : ADD_STAGE_LABELS[formTarget?.category ?? DealStageCategory.PIPELINE]}
        description={DEAL_STAGE_CATEGORY_DESCRIPTIONS[formTarget?.category ?? DealStageCategory.PIPELINE]}
        initialValues={{
          name: formTarget?.stage?.name ?? "",
          color: formTarget?.stage?.color ?? NEW_STAGE_COLOR,
        }}
        saving={isCreating || isUpdating}
        onOpenChange={(open) => {
          if (!open) {
            setFormTarget(null);
          }
        }}
        onSave={(values) => void handleSave(values)}
      />

      <DeleteDealStageDialog stage={stageToDelete} stages={activeStages} onClose={() => setStageToDelete(null)} />
    </div>
  );
}
